import Text from 'components/shared/Text'
import { FormattedMessage } from 'react-intl'
import Box from 'components/shared/Box'
import { useApp } from 'store/steps'
import {
    ProductBundlePresenter,
    TicketDto,
    useListTicketPriceByTypesQuery,
} from 'store/api-slice/hooks'
import TicketTypeRadio from 'components/shared/RadioButtons/TicketTypeRadio'
import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import { useAutoScroll } from 'utils/hooks'
import { Person, TicketInfo, Zone } from 'store/api-slice/types'
import Loader from 'components/shared/Loader'
import { useTickets } from 'store/ticket-slice/hooks'
import { useCart } from 'store/cart-slice/hooks'
import { DefaultTheme } from 'styled-components'
import { useTicketPromotions } from 'routes/Tickets/utils/useTicketPromotions'
import TicketsWithAccommodationInCartPromoInfo from 'components/shared/Promotions/components/TicketsWithAccommodationInCartPromoInfo'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { NoTicketsTile } from './NoTicketsTile'

interface TicketTypeProps {
    index: number
    person: Person
    zone: Zone
    boxId: string
    theme?: DefaultTheme
}

const TicketType = ({ index, person, zone, boxId, theme }: TicketTypeProps) => {
    const { shouldPromoDisableTicketsBundles, isBlackPromoForCreator } =
        usePromotions()
    const { renderActivePromotions } = useTicketPromotions()
    const { calculateScroll, animatePendingScroll } = useAutoScroll()
    const { language, isCreatorAccommodation } = useApp()
    const [chosenType, setChosenType] = useState<string | null>(null)
    const { ticketBoxes, dates, areTicketsUpdating } = useAppSelector(
        (state) => state.ticket
    )
    const {
        addTicketToCart,
        addSeniorPackageToCart,
        removeTicketFromCart,
        isTicketInCart,
    } = useTickets(boxId)
    const { isCartFetching, isStepEmpty } = useCart()

    const currentTicket = ticketBoxes.find((box) => box.boxId === boxId)
    const { ticketTime } = currentTicket || {}

    const { data: ticketTypes, isFetching } = useListTicketPriceByTypesQuery({
        _locale: language,
        person,
        startDate: dates[0],
        endDate: dates.length > 1 ? dates[dates.length - 1] : undefined,
        zone,
    })

    const { tickets, productBundles } = ticketTypes || {}

    const localId = `tickets-${index}-type`

    const handleChooseBundledTicketType = (bundle: ProductBundlePresenter) => {
        const { productId } = bundle
        if (!productId) return

        setChosenType(productId)

        if (productId === chosenType) return

        if (isTicketInCart) {
            removeTicketFromCart(true).then(() => addBundle(bundle))
        } else {
            addBundle(bundle)
        }
    }

    const handleChooseTicketType = (ticket: TicketDto) => {
        const { time } = ticket
        if (!time) return

        setChosenType(time)

        if (time === chosenType) return

        if (isTicketInCart) {
            removeTicketFromCart(true).then(() => addTicket(ticket))
        } else {
            addTicket(ticket)
        }
    }

    const bundlesTypes = useMemo(
        () =>
            productBundles?.map((bundle) => {
                const {
                    productId,
                    name,
                    unitPrice,
                    descriptions,
                    status,
                    dates: bundleDates,
                } = bundle

                if (!productId || !name || !unitPrice || !descriptions)
                    return null

                // TODO: all info section to unify in all products and bundles
                // both in backend and frontend

                const descriptionsToInfo: TicketInfo[] = descriptions?.map(
                    ({ name, icon, description }) => ({
                        title: name,
                        iconName: icon,
                        description,
                    })
                )

                return (
                    <TicketTypeRadio
                        key={productId}
                        id="type"
                        isChecked={productId === chosenType}
                        label={name}
                        onClick={() =>
                            areTicketsUpdating || isCartFetching
                                ? null
                                : handleChooseBundledTicketType(bundle)
                        }
                        option={productId}
                        price={unitPrice * dates.length}
                        zone={zone}
                        info={descriptionsToInfo}
                        numberOfDays={
                            bundleDates?.length === dates.length
                                ? dates.length
                                : 0
                        }
                        theme={theme}
                        testId={`ticket-${index}-select-type-bundle`}
                        isPromotionActive={
                            bundle.status ? bundle.status.active : undefined
                        }
                        additionalText={
                            bundle.status ? bundle.status.message : undefined
                        }
                        status={status}
                        isBundle={true}
                        isItNotCombinedWithPromotions={isBlackPromoForCreator}
                    />
                )
            }),
        [
            productBundles,
            chosenType,
            zone,
            index,
            handleChooseBundledTicketType,
            areTicketsUpdating,
            isCartFetching,
        ]
    )

    const shouldRenderTickets = useMemo(
        () =>
            tickets?.some(
                (ticket) =>
                    ticket?.status?.active === true && ticket?.prices?.length
            ),
        [tickets]
    )

    const ticketsTypes = useMemo(
        () =>
            tickets?.map((ticket) => {
                const { title, info, totalPrice, time, zone, prices, status } =
                    ticket

                if (
                    !title ||
                    !zone ||
                    !time ||
                    !prices ||
                    totalPrice === undefined
                )
                    return null

                return (
                    <TicketTypeRadio
                        key={time}
                        id="type"
                        isChecked={time === chosenType}
                        label={title}
                        onClick={() =>
                            areTicketsUpdating || isCartFetching
                                ? null
                                : handleChooseTicketType(ticket)
                        }
                        option={time}
                        price={totalPrice}
                        zone={zone}
                        info={info}
                        numberOfDays={
                            prices.length === dates.length ? dates.length : 0
                        }
                        theme={theme}
                        testId={`ticket-${index}-select-type-${time}`}
                        status={status}
                    />
                )
            }),
        [
            tickets,
            chosenType,
            index,
            areTicketsUpdating,
            isCartFetching,
            handleChooseTicketType,
        ]
    )

    useEffect(() => {
        if (isFetching || !ticketTypes?.tickets) return
        animatePendingScroll(localId)
    }, [isFetching])

    useEffect(() => {
        setChosenType(ticketTime || null)
    }, [ticketTime])

    const addTicket = (ticket: TicketDto) => {
        addTicketToCart({ ticket, person }).then(() => {
            calculateScroll({
                trigerringSection: 'tickets',
                trigerringAction: 'modify',
                index,
                parameter: 'type',
            })
        })
    }

    const addBundle = (bundle: ProductBundlePresenter) => {
        addSeniorPackageToCart([bundle]).then(() => {
            calculateScroll({
                trigerringSection: 'tickets',
                trigerringAction: 'modify',
                index,
                parameter: 'type',
            })
        })
    }

    return (
        <Box gap={[16, 24]} isFullWidth={true} id={localId}>
            <Text variant="heading3" color="grayscale_900" theme={theme}>
                <FormattedMessage
                    id="tickets.type_section_caption"
                    defaultMessage="Wybierz rodzaj biletu"
                />
            </Text>

            {renderActivePromotions({ key: 'creator' })}

            {isCreatorAccommodation && !isStepEmpty('accommodation') && (
                <TicketsWithAccommodationInCartPromoInfo />
            )}

            <Box gap={[8, 16]} align="stretch" isFullWidth>
                {isFetching ? (
                    <Loader />
                ) : (
                    <>
                        {!shouldPromoDisableTicketsBundles && bundlesTypes}
                        {shouldRenderTickets ? ticketsTypes : <NoTicketsTile />}
                    </>
                )}
            </Box>
        </Box>
    )
}

export default TicketType
